<template>
  <v-card :color="$vuetify.theme.dark ? 'blue-darken-2 ' : 'white-lighten-2'">
    <v-row class="no-gutters">
      <v-col cols="12" sm="6" md="6" lg="3">
        <v-text-field v-model="search" @input="debounceSearch" prepend-icon="mdi-magnify" label="Search here ..."
          single-line solo hide-details>
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="2">
        <v-select solo :items="allAccounts()" v-model="trading_account_type_id" @input="accountFilter" append-icon="mdi-chevron-down"
          label="Account Types" class="custom-select" item-text="account_type_display" item-value="id"></v-select>
      </v-col>

      <v-col cols="12" sm="6" md="6" lg="2">
        <v-dialog ref="dialog" v-model="modal" :return-value.sync="date" width="290px" class="">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="selectedDatesText" placeholder="Date filter" prepend-icon="mdi-calendar"
              append-icon="mdi-chevron-down" readonly v-bind="attrs" v-on="on" solo
              class="custom-select"></v-text-field>
          </template>
          <v-date-picker v-model="selectedDates" class="custom-date-picker" scrollable range>
            <v-spacer></v-spacer>
            <v-btn :color="$vuetify.theme.dark ? '#ff567f' : '#ff567f'" text @click="cancelSelection">
              Cancel
            </v-btn>
            <v-btn :color="$vuetify.theme.dark ? '#fff' : '#131c29'" text @click="confirmSelection">
              OK
            </v-btn>
          </v-date-picker>
        </v-dialog>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-btn @click="fetchData" :disabled="isLoading" small :ripple="false" tile elevation="0" class="custom-btn"
          height="39" style="margin-top: 29px; color: white" color="#039de9">
          <template v-if="isLoading">
            <v-progress-circular indeterminate size="15" color="white" style="margin-right: 5px;"></v-progress-circular>
            Fetching<span class="dots"></span>
          </template>
          <template v-else>
            <v-icon color="#fff" left>mdi-refresh</v-icon>
            Refresh
          </template>
        </v-btn>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="2">
        <ul>
          <li>
            <span>Total Lot:</span>
            <span>{{ formattedAllNumber(summaries?.totalVolume) || 0 }}</span>
          </li>
          <li>
            <span class="profit-loss">Profit / Loss:</span>
            <span>{{ formattedAllNumber(summaries?.totalProfit) || 0 }}</span>
          </li>
        </ul>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12">
        <v-card class="rounded-0 elevation-0" :color="$vuetify.theme.dark ? '#172233' : '#E8F1F8'">
          <v-sheet class="transactionTable depositTableForTransaction"
            :color="$vuetify.theme.dark ? '#1A2A3E ' : '#F1F7FB'">
            <v-data-table :headers="headers" :items="trades" :server-items-length="pagination?.total || 10"
              :options.sync="filterOptions" :loading="isLoading" :items-per-page="filterOptions.itemsPerPage"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 200],
                'items-per-page-text': 'Rows per page:'
              }" @update:options="handleOptionsChange" disable-sort>
              <template v-slot:loading>
                <div class="d-flex align-center justify-center pa-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <span class="ml-2">Loading data...</span>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="d-flex align-center justify-center pa-4">
                  <v-icon color="secondary">mdi-information</v-icon>
                  <span class="ml-2">No data found</span>
                </div>
              </template>
              <template v-slot:no-results>
                <div class="d-flex align-center justify-center pa-4">
                  <v-icon color="secondary">mdi-magnify</v-icon>
                  <span class="ml-2">No matching data found</span>
                </div>
              </template>
              <template v-slot:item.fullname="{ item }">
                <router-link v-if="$canRoute('registered-user?tab=1') === 1" class="text-capitalize text--ash" :to="`/registered-user-details/${item?.client_account_id}`">
                  {{ `${item.fname} ${item.lname} (${item?.client_account_id})` }}
                </router-link>
                <span v-else class="text-capitalize text--ash">{{ `${item.fname} ${item.lname} (${item?.client_account_id})` }}</span>
              </template>
              <template v-slot:item.Login="{ item }">
                {{ item?.Login + " (" + (item.tradingaccounttypes.account_type_display) +
                  ")" }}
              </template>

              <template v-slot:item.Lots="{ item }">
                {{ item?.VolumeClosed / 10000 }}
              </template>

              <template v-slot:item.SLTP="{ item }">
                {{ item?.PriceSL + " - " + item?.PriceTP }}
              </template>

              <template v-slot:item.OpenTime="{ item }">
                {{ formatSmallTradeDate(item?.OpenTime) }}
              </template>

              <template v-slot:item.CloseTime="{ item }">
                {{ formatSmallTradeDate(item?.CloseTime) }}
              </template>

              <template v-slot:item.Action="{ item }">
                <span :style="{
                  color: item?.Action === 0 ? '#4169E1' : item?.Action === 1 ? '#FF4040' : 'inherit'
                }">
                  {{ item?.Action === 0 ? "Buy" : item?.Action === 1 ? "Sell" : null }}
                </span>
              </template>
            </v-data-table>
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapState, mapActions } from "vuex";
import {
  ACCOUNTTYPES__ACTIONS,
  SHORTDURATIONTRADES__ACTIONS
} from "@/store/action-types";
import { formatNumber } from "@/_helpers/formatNumber";
import toast from "@/services/Notification";
import { formatSmallTradeDate } from "@/_helpers/formateDate";

export default {
  data() {
    return {
      date: {},
      search: "",
      searchTimeout: null,
      selectedDates: [],
      selectedDatesText: null,
      filterOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      trading_account_type_id: null,
      headers: [
        { text: "Name", align: "start", value: "fullname" },
        { text: "Account", value: "Login" },
        { text: "Ticket", value: "PositionID" },
        { text: "OpenTime", value: "OpenTime" },
        { text: "CloseTime", value: "CloseTime" },
        { text: "Time Difference", value: "TimeDiff" },
        { text: "Lots", value: "Lots" },
        { text: "Symbol", value: "Symbol" },
        { text: "Open Price", value: "OpenPrice" },
        { text: "Closed Price", value: "ClosePrice" },
        { text: "Profit", value: "Profit" },
        { text: "SL-TP", value: "SLTP" },
        { text: "Equity", value: "Equity" },
        { text: "Type", value: "Action" },
      ],
      modal: false,
    };
  },

  computed: {
    ...mapState("accounttypes", ["accounttypes"]),
    ...mapState("shortdurationtrades", [
      "trades",
      "pagination",
      "summaries",
      "isLoading",
    ]),
    ...mapState("admins", ["loggedAdmin"]),
  },

  methods: {
    formatSmallTradeDate,
    ...mapActions("shortdurationtrades", [SHORTDURATIONTRADES__ACTIONS.SHORTDURATIONTRADES]),
    ...mapActions("accounttypes", [ACCOUNTTYPES__ACTIONS.ACCOUNTTYPES]),

    formattedAllNumber(num) {
      return formatNumber(num || 0);
    },

    async fetchData() {
      const parameter = {
        page: this.filterOptions.page || 1,
        limit: this.filterOptions.itemsPerPage || 10,
        search: this.search,
        trading_account_type_id: this.trading_account_type_id || null,
      };

      if (this.selectedDates?.length === 2) {
        parameter.duration = `${this.selectedDates[0]}to${this.selectedDates[1]}`;
      }

      await this[SHORTDURATIONTRADES__ACTIONS.SHORTDURATIONTRADES](parameter);
    },

    // allAccounts() {
    //   const accTypes = this.accounttypes
    //     ? this.accounttypes.map((item) => ({
    //         id: item.id,
    //         account_type_display: item.account_type_display,
    //       }))
    //     : [];
    //   return [{ id: "All", account_type_display: "All" }, ...accTypes];
    // },
    allAccounts() {
      return [{ id: "All", account_type_display: "All" }, ...this.accounttypes];
    },

    async handleSearch(value) {
      await this.fetchData();
    },

    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.handleSearch();
      }, 500);
    },

    handleOptionsChange(options) {
      this.fetchData();
    },

    getCurrentDate() {
      return new Date().toISOString().split("T")[0];
    },

    cancelSelection() {
      this.modal = false;
      this.selectedDates = [];
      this.selectedDatesText = null
      this.fetchData();
    },

    confirmSelection() {
      if (this.selectedDates.length !== 2) {
        toast.error("Please select both start and end dates");
        return;
      }

      let [startDate, endDate] = this.selectedDates;
      if (new Date(startDate) > new Date(endDate)) {
        [startDate, endDate] = [endDate, startDate];
      }

      this.selectedDatesText = `${startDate} ~ ${endDate}`;
      this.modal = false;
      this.filterOptions.page = 1;
      this.fetchData();
    },

    accountFilter() {
      this.filterOptions.page = 1;
      this.fetchData();
    },
  },
  async created() {
    await this.fetchData()
    await this[ACCOUNTTYPES__ACTIONS.ACCOUNTTYPES]();
  },
};
</script>

<style scoped>
.dots::after {
  content: "";
  display: inline-block;
  animation: dotsAnimation 1.5s steps(4, end) infinite;
}

@keyframes dotsAnimation {
  0% {
    content: "";
  }

  25% {
    content: ".";
  }

  50% {
    content: "..";
  }

  75% {
    content: "...";
  }

  100% {
    content: "";
  }
}
</style>